import { BlockType } from '../lib/types';

/** DMN 1.3 modelling symbol id (Famla Canvas — PRD §4). */
export type DmnSymbolId =
  | 'decision'
  | 'inputData'
  | 'businessKnowledgeModel'
  | 'knowledgeSource'
  | 'decisionService'
  | 'informationRequirement'
  | 'knowledgeRequirement'
  | 'authorityRequirement'
  | 'textAnnotation'
  | 'association';

export type DmnCategoryId = 'nodes' | 'requirements' | 'artifacts';
export type DmnSymbolKind = 'node' | 'connector' | 'artifact';

export interface DmnSymbolDefinition {
  id: DmnSymbolId;
  label: string;
  xmlType: string;
  category: DmnCategoryId;
  kind: DmnSymbolKind;
  blockType: BlockType;
  defaultText: string;
  colorToken: 'neutral' | 'orange' | 'blue' | 'purple';
  /** Row 1 always-visible vs expandable "More" (PRD §7.2). */
  row: 1 | 2;
  draggable: boolean;
}

export interface DmnCategoryDefinition {
  id: DmnCategoryId;
  label: string;
  symbols: DmnSymbolDefinition[];
}

const kindBlockType = (kind: DmnSymbolKind, id: DmnSymbolId): BlockType => {
  if (id === 'textAnnotation') return 'note';
  if (kind === 'node') return 'process';
  return 'note';
};

const symbol = (
  id: DmnSymbolId,
  label: string,
  xmlType: string,
  category: DmnCategoryId,
  kind: DmnSymbolKind
): DmnSymbolDefinition => {
  const row1 = new Set<DmnSymbolId>(['decision', 'inputData', 'informationRequirement']);
  return {
    id,
    label,
    xmlType,
    category,
    kind,
    blockType: kindBlockType(kind, id),
    defaultText: label,
    colorToken: kind === 'connector' ? 'blue' : kind === 'artifact' ? 'neutral' : 'purple',
    row: row1.has(id) ? 1 : 2,
    draggable: kind !== 'connector',
  };
};

export const DMN_SYMBOLS: Record<DmnSymbolId, DmnSymbolDefinition> = {
  decision: symbol('decision', 'Decision', 'dmn:decision', 'nodes', 'node'),
  inputData: symbol('inputData', 'Input Data', 'dmn:inputData', 'nodes', 'node'),
  businessKnowledgeModel: symbol('businessKnowledgeModel', 'Business Knowledge Model', 'dmn:businessKnowledgeModel', 'nodes', 'node'),
  knowledgeSource: symbol('knowledgeSource', 'Knowledge Source', 'dmn:knowledgeSource', 'nodes', 'node'),
  decisionService: symbol('decisionService', 'Decision Service', 'dmn:decisionService', 'nodes', 'node'),
  informationRequirement: symbol('informationRequirement', 'Information Requirement', 'dmn:informationRequirement', 'requirements', 'connector'),
  knowledgeRequirement: symbol('knowledgeRequirement', 'Knowledge Requirement', 'dmn:knowledgeRequirement', 'requirements', 'connector'),
  authorityRequirement: symbol('authorityRequirement', 'Authority Requirement', 'dmn:authorityRequirement', 'requirements', 'connector'),
  textAnnotation: symbol('textAnnotation', 'Text Annotation', 'dmn:textAnnotation', 'artifacts', 'artifact'),
  association: symbol('association', 'Association', 'dmn:association', 'artifacts', 'connector'),
};

export const DMN_CATEGORIES: DmnCategoryDefinition[] = [
  {
    id: 'nodes',
    label: 'Nodes',
    symbols: Object.values(DMN_SYMBOLS).filter((s) => s.category === 'nodes'),
  },
  {
    id: 'requirements',
    label: 'Requirements',
    symbols: Object.values(DMN_SYMBOLS).filter((s) => s.category === 'requirements'),
  },
  {
    id: 'artifacts',
    label: 'Artifacts',
    symbols: Object.values(DMN_SYMBOLS).filter((s) => s.category === 'artifacts'),
  },
];

/** Progressive disclosure: Row 1 always visible; rest under More. Connectors deferred. */
export const DMN_ROW1_SYMBOLS: DmnSymbolId[] = ['decision', 'inputData', 'businessKnowledgeModel'];

/** Palette-facing symbols only (connectors / line tools come later). */
export function getDmnPaletteSymbols(): DmnSymbolDefinition[] {
  return Object.values(DMN_SYMBOLS).filter((s) => s.kind !== 'connector');
}

export function getDmnSymbol(id: DmnSymbolId | string | undefined): DmnSymbolDefinition | undefined {
  if (!id) return undefined;
  return DMN_SYMBOLS[id as DmnSymbolId];
}

export function isDmnSymbolId(value: string): value is DmnSymbolId {
  return value in DMN_SYMBOLS;
}

export function getDmnDropDimensions(id: DmnSymbolId): { width: number; height: number } {
  if (id === 'decisionService') return { width: 180, height: 100 };
  if (id === 'textAnnotation') return { width: 140, height: 60 };
  return { width: 160, height: 80 };
}
